import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_ORG_API ?? '/api/v1';

export type OrgStatus = 'active' | 'suspended' | 'archived' | 'deleting';

export type OrgBranding = {
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string;
  accent_color?: string;
  display_name?: string;
  theme?: string;
};

export type OrgProfile = {
  legal_name?: string;
  registration_number?: string;
  gst_number?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  language?: string;
  date_format?: string;
  number_format?: string;
  website?: string;
  industry?: string;
};

export type OrgContact = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  is_primary: boolean;
};

export type OrgAddress = {
  id: string;
  label?: string;
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postal_code?: string;
  country: string;
  is_primary: boolean;
};

export type OrgPreferences = {
  notifications_enabled: boolean;
  dashboard_layout?: string;
  extra?: Record<string, string>;
};

export type Organization = {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  status: OrgStatus;
  description?: string;
  metadata?: Record<string, string>;
  settings?: Record<string, string>;
  configuration?: Record<string, string>;
  branding?: OrgBranding;
  profile?: OrgProfile;
  preferences?: OrgPreferences;
  tags?: string[];
  contacts?: OrgContact[];
  addresses?: OrgAddress[];
  created_at: string;
  updated_at: string;
  archived_at?: string;
  suspended_at?: string;
};

export type SettingsBundle = {
  settings: Record<string, string>;
  configuration: Record<string, string>;
  preferences: OrgPreferences;
};

export type ActivityEntry = {
  id: string;
  action: string;
  actor_id?: string;
  summary: string;
  metadata?: Record<string, unknown>;
  created_at: string;
};

export type AuditEntry = {
  id: string;
  action: string;
  actor_id?: string;
  outcome: string;
  resource?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
};

export type SearchResult = {
  results: Organization[];
  count: number;
};

export type OrgDashboard = {
  organization: Organization;
  contact_count: number;
  address_count: number;
  document_count: number;
  recent_activity: ActivityEntry[];
  status: OrgStatus;
};

export type CreateOrganizationInput = {
  name: string;
  slug?: string;
  description?: string;
  metadata?: Record<string, string>;
  settings?: Record<string, string>;
  configuration?: Record<string, string>;
  branding?: OrgBranding;
  profile?: OrgProfile;
  preferences?: OrgPreferences;
  tags?: string[];
  contacts?: OrgContact[];
  addresses?: OrgAddress[];
};

export type UpdateOrganizationInput = Partial<CreateOrganizationInput>;

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function orgFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Organizations ${path} failed: ${res.status} ${text}`);
  }
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const orgApi = {
  list: (params?: { status?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    const query = qs.toString();
    return orgFetch<Organization[]>(`/organizations${query ? `?${query}` : ''}`);
  },

  search: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params);
    return orgFetch<SearchResult>(`/organizations/search?${qs.toString()}`);
  },

  get: (id: string) => orgFetch<Organization>(`/organizations/${id}`),

  create: (input: CreateOrganizationInput) =>
    orgFetch<Organization>('/organizations', { method: 'POST', body: JSON.stringify(input) }),

  update: (id: string, input: UpdateOrganizationInput) =>
    orgFetch<Organization>(`/organizations/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  patch: (id: string, input: Record<string, unknown>) =>
    orgFetch<Organization>(`/organizations/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  suspend: (id: string) => orgFetch<Organization>(`/organizations/${id}/suspend`, { method: 'POST' }),

  activate: (id: string) => orgFetch<Organization>(`/organizations/${id}/activate`, { method: 'POST' }),

  archive: (id: string) => orgFetch<Organization>(`/organizations/${id}/archive`, { method: 'POST' }),

  restore: (id: string) => orgFetch<Organization>(`/organizations/${id}/restore`, { method: 'POST' }),

  remove: (id: string) => orgFetch<Organization>(`/organizations/${id}`, { method: 'DELETE' }),

  getSettings: (id: string) => orgFetch<SettingsBundle>(`/organizations/${id}/settings`),

  updateSettings: (id: string, input: Partial<SettingsBundle>) =>
    orgFetch<SettingsBundle>(`/organizations/${id}/settings`, { method: 'PUT', body: JSON.stringify(input) }),

  getActivity: (id: string, limit = 50) =>
    orgFetch<ActivityEntry[]>(`/organizations/${id}/activity?limit=${limit}`),

  getAudit: (id: string, limit = 50) =>
    orgFetch<AuditEntry[]>(`/organizations/${id}/audit?limit=${limit}`),

  getDashboard: (id: string) => orgFetch<OrgDashboard>(`/organizations/${id}/dashboard`),

  listContacts: (id: string) => orgFetch<OrgContact[]>(`/organizations/${id}/contacts`),

  addContact: (id: string, input: Omit<OrgContact, 'id'>) =>
    orgFetch<OrgContact>(`/organizations/${id}/contacts`, { method: 'POST', body: JSON.stringify(input) }),

  listAddresses: (id: string) => orgFetch<OrgAddress[]>(`/organizations/${id}/addresses`),

  addAddress: (id: string, input: Omit<OrgAddress, 'id'>) =>
    orgFetch<OrgAddress>(`/organizations/${id}/addresses`, { method: 'POST', body: JSON.stringify(input) }),
};
